<template>
  <PanelSlide v-model="isMenuOpen" side="start" fullscreenOnMobile>
    <template #trigger>
      <IconButton
        class="MobileMenu-trigger"
        icon="menu"
        size="lg"
        ariaLabel="User menu"
      />
    </template>

    <div class="MobileMenu-container">
      <h3 class="MobileMenu-title">
        {{ userStore.displayName || t('ui.layout.nav.account.title') }}
      </h3>

      <nav class="MobileMenu-nav">
        <div class="MobileMenu-content">
          <LangCurrencyForm
            v-if="isLangCurrencyExpanded"
            v-model:language="selectedLanguage"
            v-model:currency="selectedCurrency"
            :languageOptions
            :currencyOptions
            :currencyDisabled="isCurrencyDisabled"
            :currencyVisible="isCurrencyVisible"
            :loading="isLoading"
            @confirm="onLangCurrencyConfirm"
          />

          <Navigation
            v-else-if="userStore.isActiveUser"
            :items="links"
            :activeId
            size="sm"
            activeStyle="text"
          />
        </div>

        <div class="MobileMenu-interactions">
          <Navigation :items="secondaryItems" size="sm" />
          <PoweredBy />
        </div>
      </nav>
    </div>
  </PanelSlide>
</template>

<script setup lang="ts">
import type { NavigationItem } from '@cvo/ui/components';
import LangCurrencyForm from '~/components/domain/LangCurrencyToggler/LangCurrencyForm.vue';

const { t, tf } = useI18n();
const route = useRoute();
const { isAuthenticated, login, logout } = useAuth();
const { links, activeId } = useNavigationLinks();
const { hasStandalonePage, faqsPage } = useFaqsLink();
const appConfig = useAppConfig();
const userStore = useUserStore();

const {
  currentLanguageLabel,
  selectedLanguage,
  selectedCurrency,
  isLoading,
  languageOptions,
  currencyOptions,
  isCurrencyDisabled,
  isCurrencyVisible,
  resetSelections,
  confirmChanges,
} = useLangCurrency();

const isMenuOpen = ref(false);
const isLangCurrencyExpanded = ref(false);

const clientSupportPage = computed(() => {
  return tf('custom.meta.support.link', appConfig.clientHomePage);
});

const helpLink = computed(() => (hasStandalonePage.value ? faqsPage.value : clientSupportPage.value));

const secondaryItems = computed<NavigationItem[]>(() => [
  { id: 'langCurrency', label: currentLanguageLabel.value, icon: 'web', action: toggleLangCurrency },
  ...(isAuthenticated.value
    ? [
      {
        id: 'help',
        label: t('ui.layout.nav.account.help'),
        icon: 'help-circle',
        href: helpLink.value,
        target: hasStandalonePage.value ? undefined : '_blank',
      },
      { id: 'logout', label: t('ui.layout.nav.account.logout'), icon: 'logout', action: logout },
    ]
    : [{ id: 'login', label: t('ui.layout.nav.account.signIn'), icon: 'account', action: login }]),
]);

function toggleLangCurrency() {
  isLangCurrencyExpanded.value = !isLangCurrencyExpanded.value;
}

async function onLangCurrencyConfirm() {
  await confirmChanges();
  isLangCurrencyExpanded.value = false;
}

watch(() => route.fullPath, () => {
  isMenuOpen.value = false;
});

watch(isMenuOpen, () => {
  isLangCurrencyExpanded.value = false;
});

// Leaving the language view without confirming drops the pending selection
watch(isLangCurrencyExpanded, (expanded) => {
  if (!expanded) resetSelections();
});
</script>

<style lang="scss">
@use 'assets/scss/theme' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-base {
  .MobileMenu {
    &-trigger {
      color: vars.$organisms-panel-dropdown-icon-color;
      background-color: vars.$organisms-panel-dropdown-button-default-bg-color;
    }

    &-container {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      height: 100%;
      padding-top: vars.$dimensions-d-6;
    }

    &-title {
      color: vars.$organisms-panel-dropdown-title-color;
      @include text.heading(h5);
    }

    // Display set explicitly: the reset leaves `nav` inline
    &-nav {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      flex: 1;
      min-height: 0;
    }

    &-content {
      flex: 1;
      overflow-y: auto;
    }

    &-interactions {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;
      padding-block-start: vars.$dimensions-d-2;
      border-block-start: vars.$dimensions-d-0-25 solid vars.$color-neutral-gray-300;

      // The block's gap already spaces it from the navigation
      .PoweredBy {
        margin-block-start: 0;
      }
    }
  }
}
</style>
